import { useEffect } from 'react'

/**
 * Горизонтальные ленты — карточки категорий, курсы, истории — с мышью не
 * листались: колесо крутит страницу вниз, а полосы прокрутки в лентах скрыты,
 * и единственным способом оставалось тащить ленту курсором.
 *
 * Слушатель один на документ, а не по ленте на компонент: лент в приложении
 * около десятка, и новые появляются регулярно. Вертикальный жест над лентой
 * превращаем в горизонтальный, трекпад не трогаем — он умеет вбок сам.
 */

/** Колесо шагами по строкам (deltaMode = DOM_DELTA_LINE) — строку считаем 16px. */
const LINE_HEIGHT = 16

const isHorizontalScroller = (element: HTMLElement): boolean => {
  if (element.scrollWidth - element.clientWidth <= 1) return false

  const { overflowX, overflowY } = getComputedStyle(element)
  if (overflowX !== 'auto' && overflowX !== 'scroll') return false

  // Элемент прокручивается и вниз — вертикальный жест принадлежит ему.
  const scrollsVertically =
    (overflowY === 'auto' || overflowY === 'scroll') &&
    element.scrollHeight - element.clientHeight > 1

  return !scrollsVertically
}

const findScroller = (target: EventTarget | null): HTMLElement | null => {
  let node = target instanceof Element ? target : null

  while (node && node !== document.body) {
    if (node instanceof HTMLElement && isHorizontalScroller(node)) return node
    node = node.parentElement
  }

  return null
}

const stepFrom = (event: WheelEvent, scroller: HTMLElement): number => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * LINE_HEIGHT
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * scroller.clientWidth
  }
  return event.deltaY
}

export const useHorizontalWheelScroll = () => {
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      // Масштабирование и горизонтальный жест трекпада оставляем браузеру.
      if (event.ctrlKey) return
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return

      const scroller = findScroller(event.target)
      if (!scroller) return

      const step = stepFrom(event, scroller)
      const limit = scroller.scrollWidth - scroller.clientWidth

      // Лента докручена до края — жест отдаём странице, иначе она замирает под
      // курсором и человек не понимает, почему список не листается дальше.
      if (step < 0 && scroller.scrollLeft <= 0) return
      if (step > 0 && scroller.scrollLeft >= limit - 1) return

      event.preventDefault()
      scroller.scrollLeft = Math.min(limit, Math.max(0, scroller.scrollLeft + step))
    }

    // passive: false — иначе preventDefault не сработает и страница уедет вниз
    // вместе с лентой.
    document.addEventListener('wheel', handleWheel, { passive: false })
    return () => document.removeEventListener('wheel', handleWheel)
  }, [])
}
